/**
 * ExecutionTracker Component
 * Shows active and recent AI model executions with progress tracking.
 */
import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import {
  getActiveExecutions,
  getRecentExecutions,
  getExecutionStatus,
  cancelExecution,
  isTerminalStatus,
  getStatusDisplay,
  formatDuration,
} from '../../services/aiModels';
import TextResultModal from './TextResultModal';
import './ExecutionTracker.css';

const POLL_INTERVAL = 2000; // Poll every 2 seconds

const ExecutionTracker = ({
  patientId,
  studyId,
  executions: externalExecutions,
  onExecutionComplete,
}) => {
  const [executions, setExecutions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [textResultExecution, setTextResultExecution] = useState(null);
  const pollingRef = useRef(null);
  const dropdownRef = useRef(null);

  // Sync with external executions if provided
  useEffect(() => {
    if (externalExecutions && externalExecutions.length > 0) {
      setExecutions((prev) => {
        const existingIds = new Set(prev.map((e) => e.id));
        const newExecutions = externalExecutions.filter(
          (e) => !existingIds.has(e.id)
        );
        return [...newExecutions, ...prev];
      });
    }
  }, [externalExecutions]);

  // Load active executions on mount
  useEffect(() => {
    loadActiveExecutions();
  }, [patientId]);

  // Start polling when there are active executions
  useEffect(() => {
    const activeExecutions = executions.filter(
      (e) => !isTerminalStatus(e.status)
    );

    if (activeExecutions.length > 0) {
      startPolling();
    } else {
      stopPolling();
    }

    return () => stopPolling();
  }, [executions]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadActiveExecutions = async () => {
    setLoading(true);
    try {
      // Load all recent executions (including completed) so users can view past results
      const data = await getRecentExecutions(patientId);
      setExecutions(data);
    } catch (err) {
      console.error('Error loading executions:', err);
    } finally {
      setLoading(false);
    }
  };

  const startPolling = () => {
    if (pollingRef.current) return;

    pollingRef.current = setInterval(async () => {
      const activeExecutions = executions.filter(
        (e) => !isTerminalStatus(e.status)
      );

      for (const execution of activeExecutions) {
        try {
          const updated = await getExecutionStatus(execution.id);
          updateExecution(updated);

          // Check if just completed
          if (isTerminalStatus(updated.status)) {
            handleExecutionComplete(updated);
          }
        } catch (err) {
          console.error(`Error polling execution ${execution.id}:`, err);
        }
      }
    }, POLL_INTERVAL);
  };

  const stopPolling = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  };

  const updateExecution = (updated) => {
    setExecutions((prev) =>
      prev.map((e) => (e.id === updated.id ? updated : e))
    );
  };

  const handleExecutionComplete = (execution) => {
    if (execution.status === 'completed') {
      // For QA/text models, show text result modal instead of generic toast
      if (execution.text_output && Object.keys(execution.text_output).length > 0) {
        setTextResultExecution(execution);
        toast.success(`${execution.model_name} completed!`);
      } else {
        toast.success(`${execution.model_name} completed successfully!`);
      }
    } else if (execution.status === 'failed') {
      toast.error(
        `${execution.model_name} failed: ${execution.error_message || 'Unknown error'}`
      );
    }

    if (onExecutionComplete) {
      onExecutionComplete(execution);
    }
  };

  const handleCancel = async (execution) => {
    try {
      await cancelExecution(execution.id);
      toast.info(`Cancelled ${execution.model_name}`);
      updateExecution({ ...execution, status: 'cancelled' });
    } catch (err) {
      console.error('Error cancelling execution:', err);
      toast.error('Failed to cancel execution');
    }
  };

  const handleClearCompleted = () => {
    setExecutions((prev) =>
      prev.filter((e) => !isTerminalStatus(e.status))
    );
  };

  // Count active executions
  const activeCount = executions.filter(
    (e) => !isTerminalStatus(e.status)
  ).length;

  // Don't render if no executions
  if (executions.length === 0 && !loading) {
    return null;
  }

  return (
    <div className="execution-tracker" ref={dropdownRef}>
      {/* Main Button */}
      <button
        className={`execution-tracker-btn ${isOpen ? 'active' : ''} ${activeCount > 0 ? 'has-active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        title="AI Model Executions"
      >
        {activeCount > 0 ? (
          <i className="fa fa-spinner fa-spin"></i>
        ) : (
          <i className="fa fa-tasks"></i>
        )}
        {activeCount > 0 && (
          <span className="execution-count">{activeCount}</span>
        )}
        <i className={`fa fa-chevron-${isOpen ? 'up' : 'down'} chevron-icon`}></i>
      </button>

      {/* Text Result Modal for QA models */}
      {textResultExecution && (
        <TextResultModal
          execution={textResultExecution}
          onClose={() => setTextResultExecution(null)}
        />
      )}

      {/* Dropdown Panel */}
      {isOpen && (
        <div className="execution-dropdown">
          <div className="execution-dropdown-header">
            <span>
              <i className="fa fa-tasks"></i>
              AI Executions
              {activeCount > 0 && (
                <span className="header-badge">{activeCount} active</span>
              )}
            </span>
            {executions.some((e) => isTerminalStatus(e.status)) && (
              <button
                className="clear-completed-btn"
                onClick={handleClearCompleted}
                title="Clear completed"
              >
                <i className="fa fa-broom"></i>
              </button>
            )}
          </div>

          <div className="execution-list">
            {executions.length === 0 ? (
              <div className="execution-empty">
                <i className="fa fa-inbox"></i>
                <span>No executions</span>
              </div>
            ) : (
              executions.map((execution) => {
                const statusInfo = getStatusDisplay(execution.status);
                const isActive = !isTerminalStatus(execution.status);
                const hasTextResult = execution.text_output && Object.keys(execution.text_output).length > 0;
                const hasToolOutput = execution.output_patient_data != null;
                const isClickable = execution.status === 'completed' && (hasTextResult || hasToolOutput);

                return (
                  <div
                    key={execution.id}
                    className={`execution-item ${execution.status}${isClickable ? ' clickable' : ''}`}
                    onClick={() => isClickable && setTextResultExecution(execution)}
                    title={isClickable ? 'Click to view result' : undefined}
                  >
                    <div className="execution-item-header">
                      <i
                        className={`fa ${execution.model_icon}`}
                        style={{ color: execution.model_color }}
                      ></i>
                      <span className="execution-model-name">
                        {execution.model_name}
                      </span>
                      <span
                        className="execution-status-badge"
                        style={{ backgroundColor: statusInfo.color }}
                      >
                        <i className={`fa ${statusInfo.icon}`}></i>
                        {statusInfo.label}
                      </span>
                    </div>

                    {/* Progress bar for active executions */}
                    {isActive && (
                      <div className="execution-progress">
                        <div
                          className="execution-progress-bar"
                          style={{ width: `${execution.progress}%` }}
                        ></div>
                        <span className="execution-progress-text">
                          {execution.progress}%
                        </span>
                      </div>
                    )}

                    {/* Status message */}
                    {execution.status_message && (
                      <div className="execution-status-message">
                        {execution.status_message}
                      </div>
                    )}

                    {/* Error message for failed executions */}
                    {execution.status === 'failed' && execution.error_message && (
                      <div className="execution-error">
                        <i className="fa fa-exclamation-circle"></i>
                        {execution.error_message.substring(0, 100)}
                        {execution.error_message.length > 100 && '...'}
                      </div>
                    )}

                    {/* Footer with time and actions */}
                    <div className="execution-item-footer">
                      <span className="execution-time">
                        {isActive ? (
                          <>Started {new Date(execution.created_at).toLocaleTimeString()}</>
                        ) : execution.duration ? (
                          <>Duration: {formatDuration(execution.duration)}</>
                        ) : (
                          <>{new Date(execution.created_at).toLocaleString()}</>
                        )}
                      </span>

                      {isActive && (
                        <button
                          className="execution-cancel-btn"
                          onClick={(e) => { e.stopPropagation(); handleCancel(execution); }}
                          title="Cancel execution"
                        >
                          <i className="fa fa-stop"></i>
                          Cancel
                        </button>
                      )}
                      {isClickable && (
                        <button
                          className="execution-view-btn"
                          onClick={(e) => { e.stopPropagation(); setTextResultExecution(execution); }}
                          title="View result"
                        >
                          <i className="fa fa-eye"></i>
                          View Result
                        </button>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ExecutionTracker;
